import { List } from 'dashboard/models/list';
import { ReportTemplate } from 'dashboard/models/report-template';
import { SupportedTriggerEvent, TriggerType } from 'dashboard/models/trigger';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import { WorkflowStatus } from 'dashboard/models/workflow';
import { ApiModel, ApiResource } from 'lib/interfaces/api';

export interface PlatformNodeAttributes {
  token: string;
  'organization-id': string;
  'inbound-connections': string[];
  'outbound-connections': string[];
}

export interface PlatformNodeRelationships {}

export type PlatformNode = ApiResource<
  PlatformNodeAttributes,
  PlatformNodeRelationships,
  ApiModel.PlatformNode
>;

// Should stay in sync with PlatformNode::SUPPORTED_CONFIG_TYPES
export enum PlatformConfigType {
  AccountType = 'AccountType',
  CaseTemplate = 'CaseTemplate',
  InquiryTemplate = 'InquiryTemplate',
  InquiryTemplateVersion = 'InquiryTemplateVersion',
  List = 'List',
  ReportTemplate = 'ReportTemplate',
  ReportTemplateVersion = 'ReportTemplateVersion',
  TxnType = 'TxnType',
  VerificationTemplate = 'VerificationTemplate',
  VerificationTemplateVersion = 'VerificationTemplateVersion',
  Workflow = 'Workflow',
  WorkflowVersion = 'WorkflowVersion',
}

export type RelationDirection = 'inbound-connections' | 'outbound-connections';

interface ConnectionNodeBase {
  type: PlatformConfigType;
  token: string;
  metadata: {
    name: string;
  };
  depth: number;
  'inbound-connections': string[];
  'outbound-connections': string[];
  'permission-blocked': boolean;
}

export interface AccountTypeNode extends ConnectionNodeBase {
  type: PlatformConfigType.AccountType;
}

export interface CaseTemplateNode extends ConnectionNodeBase {
  type: PlatformConfigType.CaseTemplate;
}

export interface InquiryTemplateNode extends ConnectionNodeBase {
  type: PlatformConfigType.InquiryTemplate;
  metadata: {
    name: string;
    'version-token': string;
  };
}

export interface InquiryTemplateVersionNode extends ConnectionNodeBase {
  type: PlatformConfigType.InquiryTemplateVersion;
  metadata: {
    name: string;
    'inquiry-template-token': string;
    'is-latest-published-version': boolean;
  };
}

export interface ListNode extends ConnectionNodeBase {
  type: PlatformConfigType.List;
  metadata: {
    name: string;
    type: List['type'];
  };
}

export interface ReportTemplateNode extends ConnectionNodeBase {
  type: PlatformConfigType.ReportTemplate;
  metadata: {
    name: string;
    type: ReportTemplate['type'];
  };
}

export interface ReportTemplateVersionNode extends ConnectionNodeBase {
  type: PlatformConfigType.ReportTemplateVersion;
  metadata: {
    name: string;
    'report-template-token': string;
    'is-latest-published-version': boolean;
  };
}

export interface TxnTypeNode extends ConnectionNodeBase {
  type: PlatformConfigType.TxnType;
}

export interface VerificationTemplateNode extends ConnectionNodeBase {
  type: PlatformConfigType.VerificationTemplate;
  metadata: {
    name: string;
    type: VerificationTemplate['type'];
    'version-token': string;
  };
}

export interface VerificationTemplateVersionNode extends ConnectionNodeBase {
  type: PlatformConfigType.VerificationTemplateVersion;
  metadata: {
    name: string;
    type: VerificationTemplate['type'];
    'verification-template-token': string;
    'inquiry-template-token'?: string;
    'inquiry-template-version-token'?: string;
    'is-latest-published-version': boolean;
  };
}

export interface WorkflowNode extends ConnectionNodeBase {
  type: PlatformConfigType.Workflow;
  metadata: {
    name: string;
    status: WorkflowStatus;
    'trigger-type': TriggerType;
    'event-name': SupportedTriggerEvent | null;
  };
}

export interface WorkflowVersionNode extends ConnectionNodeBase {
  type: PlatformConfigType.WorkflowVersion;
  metadata: {
    name: string;
    'workflow-token': string;
    'is-latest-published-version': boolean;
  };
}

export interface PermissionBlockedNode
  extends Omit<
    ConnectionNodeBase,
    'token' | 'metadata' | 'inbound-connections' | 'outbound-connections'
  > {
  token: null;
  metadata: null;
  'inbound-connections': null;
  'outbound-connections': null;
  'permission-blocked': true;
}

// all attributes excluding type and permission-blocked can be potentially
// undefined due to permission blocking
export type ConnectionNode =
  | AccountTypeNode
  | CaseTemplateNode
  | InquiryTemplateNode
  | InquiryTemplateVersionNode
  | ListNode
  | PermissionBlockedNode
  | ReportTemplateNode
  | ReportTemplateVersionNode
  | TxnTypeNode
  | VerificationTemplateNode
  | VerificationTemplateVersionNode
  | WorkflowNode
  | WorkflowVersionNode;

export type VersionLevelConnectionNode =
  | InquiryTemplateVersionNode
  | ReportTemplateVersionNode
  | VerificationTemplateVersionNode
  | WorkflowVersionNode;
export type ConnectionNodesByType = { [K in PlatformConfigType]: (ConnectionNode & { type: K })[] };
